'use client';

import { createContext, useContext } from 'react';

import type { ListingVariant } from '@ps/models/lib/listing';

import type {
  VariantAttrType,
  VariantSelection,
  VariantValueType,
} from '../../functions/variantCategories';

/**
 * Shared variant + quantity state for the PDP's interactive region. Lives in
 * context because the variant-aware price renders up in Row 1 (next to the
 * name + streamed seller card) while the variant picker / quantity stepper /
 * Buy button render lower down — non-contiguous positions that all need to
 * react to the same selection. The provider (`PdpInteractive`) owns the
 * `useVariantSelection` + `useQuantitySelection` state; the leaves below
 * consume it.
 */
export type VariantQuantityContextValue = {
  selection: VariantSelection;
  selectedVariant: ListingVariant | undefined;
  onSelectAttr: (attr: VariantAttrType, value: VariantValueType) => void;
  hasVariants: boolean;
  quantity: number;
  maxQuantity: number;
  isFixedToOne: boolean;
  isSoldOut: boolean;
  setQuantity: (q: number) => void;
};

export const VariantQuantityContext =
  createContext<VariantQuantityContextValue | null>(null);

/** Read the PDP variant/quantity context. Returns null outside the provider
 *  (e.g. digital/external listings that don't mount the interactive island). */
export function useVariantQuantity(): VariantQuantityContextValue | null {
  return useContext(VariantQuantityContext);
}
